import {
	InputCheckBox,
	RiCameraLine,
	RiCameraOffLine,
	RiChat4Line,
	RiChatOffLine,
	RiDeleteBinLine,
	RiDownload2Line,
	RiEyeLine,
	RiEyeOffLine,
	RiHammerLine,
	RiIndeterminateCircleLine,
	RiMoreFill
} from "@itandi/itandi-bb-ui";
import clsx from "clsx";
import { useRouter } from "next/router";
import type React from "react";
import { SortableHandle } from "react-sortable-hoc";
import { toast } from "react-toastify";
import { useSWRConfig } from "swr";
import { Dropdown } from "~/components/common/parts/dropdown/Dropdown";
import { DropdownMenu } from "~/components/common/parts/dropdown/DropdownMenu";
import { ConfirmModal } from "~/components/common/parts/modal/ConfirmModal";
import { ReportImagesAreaBlockRemarks } from "~/components/common/reports/ReportImagesAreaBlockRemarks";
import { useOpenHandler } from "~/hooks/useOpenHandler";
import type { DealReportImage } from "~/models/DealReportImage";
import {
	imageUploadedLabel,
	operableReportImage
} from "~/models/DealReportImage";
import { isSuccess } from "~/utils/api/api_handler";
import { getReportImageSwrKey } from "~/utils/DealReportImageUtil";
import type { NskResponse } from "../../../models/Api";
import { DropdownList } from "../parts/dropdown/DropdownList";
import Styles from "./ReportImagesAreaBlock.module.scss";

type Props = {
	companyType: "company" | "vendor";
	displaySeqNo: number | null;
	handleDestroy:
		| ((reportImage: DealReportImage) => Promise<NskResponse>)
		| null;
	handleDownloadImage: (reportImage: DealReportImage) => void;
	handleUpdate: (submitValue: DealReportImage) => Promise<NskResponse>;
	handleUpdateSeq:
		| ((newOrderImages: DealReportImage[]) => Promise<void>)
		| null;
	onChangeSelectedImage: (reportImageId: number) => void;
	onPreviewOpen: () => void;
	reportImage: DealReportImage;
	selectedImageIds: readonly number[];
};

const DragHandle = SortableHandle(() => (
	<div className={Styles.DragHandleWrapper}>
		<img alt="drag handle" src="/assets/DragHandle.svg" />
	</div>
));

export function ReportImagesAreaBlock({
	reportImage,
	handleDownloadImage,
	handleUpdateSeq,
	handleUpdate,
	handleDestroy,
	companyType,
	displaySeqNo,
	onChangeSelectedImage,
	onPreviewOpen,
	selectedImageIds
}: Props) {
	const router = useRouter();
	const { mutate: globalMutate } = useSWRConfig();
	const {
		isOpen: isOpenDelete,
		handleOpen: handleOpenDelete,
		handleClose: handleCloseDelete
	} = useOpenHandler();

	const handleMutateReportImages = async (): Promise<void> => {
		await globalMutate(getReportImageSwrKey(router.asPath, reportImage.dealId));
	};

	const onClickImage = (e: React.MouseEvent): void => {
		e.preventDefault();
		onPreviewOpen();
	};
	const isVendor = companyType === "vendor";
	const operableImage = operableReportImage(reportImage, companyType);
	const isShared = reportImage.shared;
	const isOutputPdfImage = reportImage.outputPdfImage;
	const isOutputPdfNote = reportImage.outputPdfNote;
	const uploadLabel = imageUploadedLabel(isVendor, reportImage);
	const handleAcceptDestroy = async (): Promise<void> => {
		if (handleDestroy === null) {
			return;
		}
		const result = await handleDestroy(reportImage);
		if (isSuccess(result)) {
			await handleMutateReportImages();
			toast.success("削除しました");
			return;
		}
		toast.error("エラーが発生しました");
	};
	const handleAcceptUpdateShared = async (
		submitValue: boolean
	): Promise<void> => {
		const newReportImage = { ...reportImage, shared: submitValue };
		await handleUpdate(newReportImage);
		await handleMutateReportImages();
	};
	const handleAcceptUpdateOutputPdf = async (
		submitKey: "outputPdfImage" | "outputPdfNote",
		submitValue: boolean
	): Promise<void> => {
		const newReportImage = { ...reportImage, [submitKey]: submitValue };
		await handleUpdate(newReportImage);
		await handleMutateReportImages();
	};
	const handleAcceptUpdateConstruction = async (
		submitValue: "unspecified" | "targeted"
	): Promise<void> => {
		await handleUpdate({ ...reportImage, construction: submitValue });
		await handleMutateReportImages();
	};
	return (
		<div
			className={clsx(
				Styles.ImageBlockWrapper,
				reportImage.construction === "targeted" && Styles.Targeted
			)}
		>
			<div className={Styles.ImageBlock}>
				{handleUpdateSeq !== null && <DragHandle />}
				<div className={Styles.ImageWrapper}>
					<a href="" onClick={onClickImage}>
						<img
							alt="報告画像"
							className={Styles.Image}
							src={reportImage.thumbnailImageUrl ?? reportImage.imageUrl}
						/>
					</a>
					<div className={Styles.Plates}>
						{isVendor || isOutputPdfImage ? (
							<div className={Styles.Plate}>No.{displaySeqNo}</div>
						) : null}
						{uploadLabel ? (
							<div className={Styles.Plate}>{uploadLabel}</div>
						) : null}
					</div>
					{isVendor && !isShared ? (
						<p className={Styles.NotSharedPlate}>管理会社に共有されません</p>
					) : null}
					{!isVendor && !isOutputPdfImage && (
						<p className={Styles.NotOutPutPdf}>画像は報告書に出力されません</p>
					)}
					<p className={Styles.ImageCheckbox}>
						<InputCheckBox
							checked={selectedImageIds.includes(reportImage.id)}
							onChange={(): void => {
								onChangeSelectedImage(reportImage.id);
							}}
						/>
					</p>
					<div className={Styles.ButtonWrapper}>
						<Dropdown
							className={Styles.MenuDropdown}
							noBorder
							placement="bottom-start"
							title={
								<span className={Styles.MenuButton}>
									<RiMoreFill className={Styles.MenuIcon} />
								</span>
							}
							useCaret={false}
							variant="link"
						>
							<DropdownList>
								<DropdownMenu
									onClick={(): void => {
										handleDownloadImage(reportImage);
									}}
								>
									<RiDownload2Line className={Styles.DropdownMenuIcon} />
									画像をダウンロード
								</DropdownMenu>
								{operableImage ? (
									<DropdownMenu
										onClick={(): void => {
											handleAcceptUpdateConstruction(
												reportImage.construction === "targeted"
													? "unspecified"
													: "targeted"
											).then();
										}}
									>
										{reportImage.construction === "targeted" ? (
											<>
												<RiIndeterminateCircleLine
													className={Styles.DropdownMenuIcon}
												/>
												{reportImage.type === "DealInspectionReportImage" ? (
													<>施工推奨から外す</>
												) : (
													<>施工対象から外す</>
												)}
											</>
										) : (
											<>
												<RiHammerLine className={Styles.DropdownMenuIcon} />
												{reportImage.type === "DealInspectionReportImage" ? (
													<>施工推奨にする</>
												) : (
													<>施工対象にする</>
												)}
											</>
										)}
									</DropdownMenu>
								) : null}
								{isVendor && operableImage ? (
									<DropdownMenu
										onClick={(): void => {
											handleAcceptUpdateShared(!reportImage.shared);
										}}
									>
										{isShared ? (
											<>
												<RiEyeOffLine className={Styles.DropdownMenuIcon} />
												画像を共有しない
											</>
										) : (
											<>
												<RiEyeLine className={Styles.DropdownMenuIcon} />
												画像を共有する
											</>
										)}
									</DropdownMenu>
								) : null}
								{!isVendor && (
									<DropdownMenu
										onClick={(): void => {
											handleAcceptUpdateOutputPdf(
												"outputPdfImage",
												!reportImage.outputPdfImage
											).then();
										}}
									>
										{isOutputPdfImage ? (
											<>
												<RiCameraOffLine className={Styles.DropdownMenuIcon} />
												画像を報告書に含めない
											</>
										) : (
											<>
												<RiCameraLine className={Styles.DropdownMenuIcon} />
												画像を報告書に含める
											</>
										)}
									</DropdownMenu>
								)}
								{!isVendor && (
									<DropdownMenu
										className={!isOutputPdfImage ? Styles.disabled : ""}
										onClick={(): void => {
											handleAcceptUpdateOutputPdf(
												"outputPdfNote",
												!reportImage.outputPdfNote
											).then();
										}}
									>
										{isOutputPdfNote ? (
											<>
												<RiChatOffLine className={Styles.DropdownMenuIcon} />
												備考を報告書に含めない
											</>
										) : (
											<>
												<RiChat4Line className={Styles.DropdownMenuIcon} />
												備考を報告書に含める
											</>
										)}
									</DropdownMenu>
								)}
								{operableImage ? (
									<DropdownMenu onClick={handleOpenDelete}>
										<span className={Styles.DeleteLink}>
											<RiDeleteBinLine className={Styles.DropdownMenuIcon} />
											画像を削除
										</span>
									</DropdownMenu>
								) : null}
							</DropdownList>
						</Dropdown>
					</div>
					<ConfirmModal
						confirmType="delete"
						handleAccept={handleAcceptDestroy}
						handleCancel={handleCloseDelete}
						isOpen={isOpenDelete}
					>
						この画像を削除してよろしいですか？
					</ConfirmModal>
				</div>
				<div>
					<ReportImagesAreaBlockRemarks
						companyType={companyType}
						handleUpdate={handleUpdate}
						reportImage={reportImage}
					/>
				</div>
			</div>
		</div>
	);
}
